import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, MagnifyingGlass, CurrencyDollar, Receipt, CheckCircle, Clock, Warning, FilePdf, CreditCard, Link as LinkIcon, Bank, CalendarBlank, EnvelopeSimple, ListChecks, CaretDown, CaretRight, Bell, Lightning } from '@phosphor-icons/react';
import { getPayments, createPayment, updatePayment, receivePayment, getOrganizations, getDossiers, generateRecuPdf, getPaymentGatewaysConfig, stripeCheckout, paypalCheckout, getPaymentTransactions, sendBankTransferInfo, createStripePaymentLink, createPaymentSchedule, getPaymentSchedules, getSepaMandate, createSepaMandate, initiateSepaDebit, getRemindersHistory, triggerReminderScan } from '../services/api';
import { CurrencyInput } from '../components/CurrencyInput';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../utils/apiError';
import { PageHero } from '../components/motion/PageHero';

const typeFluxOptions = [
  { value: 'cotisation_ftpe', label: 'Cotisation FTPE' },
  { value: 'contribution_ess', label: 'Contribution ESS' },
  { value: 'acompte', label: 'Acompte' },
  { value: 'solde', label: 'Solde' },
  { value: 'frais_dossier', label: 'Frais de dossier' },
  { value: 'formation', label: 'Formation' },
  { value: 'autre', label: 'Autre' },
];

const statutOptions = [
  { value: 'en_attente', label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'partiel', label: 'Partiellement réglé', color: 'bg-orange-100 text-orange-800' },
  { value: 'recu', label: 'Reçu', color: 'bg-green-100 text-green-800' },
  { value: 'rapproche', label: 'Rapproché', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'anomalie', label: 'En anomalie', color: 'bg-red-100 text-red-800' },
  { value: 'annule', label: 'Annulé', color: 'bg-gray-100 text-gray-800' },
];

const modeReglementOptions = [
  { value: 'virement', label: 'Virement bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'especes', label: 'Espèces' },
  { value: 'carte', label: 'Carte bancaire' },
  { value: 'prelevement', label: 'Prélèvement' },
  { value: 'sepa_core', label: 'Prélèvement SEPA CORE' },
  { value: 'sepa_b2b', label: 'Prélèvement SEPA B2B' },
];

export function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('');
  const [filterType, setFilterType] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [receiveAmount, setReceiveAmount] = useState('');
  const [receiveReference, setReceiveReference] = useState('');
  const [receiveDate, setReceiveDate] = useState('');
  const [receiveMode, setReceiveMode] = useState('');
  const [formData, setFormData] = useState({
    type_flux: '',
    organization_id: '',
    dossier_ids: [],
    montant_attendu: '',
    date_echeance: '',
    mode_reglement: '',
    commentaire: '',
  });
  const [gateways, setGateways] = useState({});
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  // New dialogs
  const [linkDialog, setLinkDialog] = useState({ open: false, payment: null, email: '', type: '' });
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    organization_id: '', dossier_id: '', total_amount: '', type_flux: 'cotisation_ftpe',
    nb_echeances: '3', acompte_type: 'percent', acompte_value: '30', start_date: '', commentaire: '',
  });
  const [schedules, setSchedules] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [sepaDialog, setSepaDialog] = useState({ open: false, payment: null, sepaType: null });
  const [sepaMandates, setSepaMandates] = useState([]);
  const [selectedMandate, setSelectedMandate] = useState('');

  // Echeanciers tab
  const [activeTab, setActiveTab] = useState('paiements');
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [expandedSchedule, setExpandedSchedule] = useState(null);
  const [remindersHistory, setRemindersHistory] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [triggeringReminders, setTriggeringReminders] = useState(false);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (filterType) params.type_flux = filterType;
      
      const [paymentsRes, orgsRes, dossiersRes, gwRes] = await Promise.all([
        getPayments(params),
        getOrganizations(),
        getDossiers(),
        getPaymentGatewaysConfig().catch(() => ({ data: { gateways: {} } })),
      ]);
      setPayments(paymentsRes.data.items || []);
      setOrganizations(orgsRes.data.items || []);
      setDossiers(dossiersRes.data.items || []);
      setGateways(gwRes.data.gateways || {});
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterStatut, filterType]);

  useEffect(() => {
    if (activeTab === 'echeanciers') {
      fetchSchedules();
      fetchReminders();
    }
  }, [activeTab]);

  const fetchSchedules = async () => {
    setSchedulesLoading(true);
    try {
      const res = await getPaymentSchedules();
      setSchedules(res.data.items || []);
    } catch { } finally {
      setSchedulesLoading(false);
    }
  };

  const fetchReminders = async () => {
    setRemindersLoading(true);
    try {
      const res = await getRemindersHistory({ limit: 30 });
      setRemindersHistory(res.data.items || []);
    } catch { } finally {
      setRemindersLoading(false);
    }
  };

  const handleTriggerReminders = async () => {
    setTriggeringReminders(true);
    try {
      const res = await triggerReminderScan();
      const d = res.data;
      toast.success(`Scan termine : ${d.sent_email} email(s), ${d.sent_sms} SMS, ${d.skipped} ignore(s)`);
      fetchReminders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur scan relances');
    } finally {
      setTriggeringReminders(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        montant_attendu: parseFloat(formData.montant_attendu),
      };
      // Compat legacy : conserve dossier_id sur la première sélection
      if (data.dossier_ids && data.dossier_ids.length > 0) {
        data.dossier_id = data.dossier_ids[0];
      } else {
        delete data.dossier_id;
      }
      await createPayment(data);
      toast.success('Paiement créé');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Erreur'));
    }
  };

  const resetForm = () => {
    setFormData({
      type_flux: '',
      organization_id: '',
      dossier_ids: [],
      montant_attendu: '',
      date_echeance: '',
      mode_reglement: '',
      commentaire: '',
    });
  };

  const openReceiveDialog = (payment) => {
    setSelectedPayment(payment);
    setReceiveAmount((payment.montant_attendu - (payment.montant_recu || 0)).toString());
    setReceiveReference('');
    setReceiveDate(new Date().toISOString().split('T')[0]);
    setReceiveMode(payment.mode_reglement || '');
    setReceiveDialogOpen(true);
  };

  const handleReceive = async () => {
    if (!selectedPayment) return;
    try {
      await receivePayment(selectedPayment.id, parseFloat(receiveAmount), receiveReference, receiveDate, receiveMode);
      toast.success('Paiement enregistré');
      setReceiveDialogOpen(false);
      setSelectedPayment(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const getOrgName = (id) => organizations.find(o => o.id === id)?.raison_sociale || '-';
  const getDossierNum = (id) => dossiers.find(d => d.id === id)?.numero_dossier || '-';
  const getDossierNums = (payment) => {
    const ids = payment?.dossier_ids && payment.dossier_ids.length > 0
      ? payment.dossier_ids
      : (payment?.dossier_id ? [payment.dossier_id] : []);
    return ids.map(getDossierNum).filter(n => n && n !== '-');
  };
  const fmtEUR = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const getStatutBadge = (statut) => {
    const s = statutOptions.find(opt => opt.value === statut);
    return s ? <Badge className={s.color}>{s.label}</Badge> : <Badge>{statut}</Badge>;
  };

  const hasGateway = gateways.stripe?.enabled || gateways.paypal?.enabled;

  const handleOnlinePayment = async (payment, gateway) => {
    setCheckoutLoading(`${payment.id}-${gateway}`);
    try {
      const originUrl = window.location.origin;
      const checkoutFn = gateway === 'stripe' ? stripeCheckout : paypalCheckout;
      const res = await checkoutFn({ payment_id: payment.id, origin_url: originUrl });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la creation du paiement en ligne';
      toast.error(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleDownloadRecu = async (paymentId) => {
    try {
      const res = await generateRecuPdf(paymentId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recu_${paymentId.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Recu PDF telecharge');
    } catch {
      toast.error('Erreur generation PDF');
    }
  };

  // ---- Send link (Stripe link or Bank transfer) ----
  const handleSendLink = async () => {
    if (!linkDialog.email || !linkDialog.payment) return;
    setActionLoading('link');
    try {
      if (linkDialog.type === 'stripe') {
        await createStripePaymentLink({ payment_id: linkDialog.payment.id, email: linkDialog.email, origin_url: window.location.origin });
        toast.success('Lien de paiement Stripe envoye');
      } else {
        await sendBankTransferInfo({ payment_id: linkDialog.payment.id, email: linkDialog.email });
        toast.success('Informations de virement envoyees');
      }
      setLinkDialog({ open: false, payment: null, email: '', type: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally {
      setActionLoading(null);
    }
  };

  // ---- SEPA debit ----
  const openSepaDialog = async (payment, sepaType = null) => {
    setSepaDialog({ open: true, payment, sepaType });
    try {
      const res = await getSepaMandate();
      let orgMandates = (res.data.items || []).filter(m => m.organization_id === payment.organization_id && m.statut === 'actif');
      if (sepaType) {
        orgMandates = orgMandates.filter(m => m.type_sepa === sepaType);
      }
      setSepaMandates(orgMandates);
      setSelectedMandate(orgMandates[0]?.id || '');
    } catch { setSepaMandates([]); }
  };

  const handleSepaDebit = async () => {
    if (!selectedMandate || !sepaDialog.payment) return;
    setActionLoading('sepa');
    try {
      await initiateSepaDebit({ mandate_id: selectedMandate, payment_id: sepaDialog.payment.id });
      toast.success('Prelevement SEPA initie');
      setSepaDialog({ open: false, payment: null });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur SEPA');
    } finally {
      setActionLoading(null);
    }
  };

  // ---- Schedule creation ----
  const handleCreateSchedule = async () => {
    if (!scheduleForm.organization_id || !scheduleForm.total_amount) {
      toast.error('Organisation et montant requis');
      return;
    }
    setActionLoading('schedule');
    try {
      await createPaymentSchedule({
        ...scheduleForm,
        total_amount: parseFloat(scheduleForm.total_amount),
        nb_echeances: parseInt(scheduleForm.nb_echeances),
        acompte_value: parseFloat(scheduleForm.acompte_value || '0'),
      });
      toast.success('Echeancier cree');
      setScheduleDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur creation echeancier');
    } finally {
      setActionLoading(null);
    }
  };

  // Calculate totals
  const totalAttendu = payments.reduce((acc, p) => acc + (p.montant_attendu || 0), 0);
  const totalRecu = payments.reduce((acc, p) => acc + (p.montant_recu || 0), 0);
  const totalEnAttente = payments.filter(p => p.statut === 'en_attente').reduce((acc, p) => acc + (p.montant_attendu || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Paiements & Contributions"
        subtitle="Suivi financier, échéanciers et relances automatisées"
        icon={<CurrencyDollar size={24} weight="duotone" className="text-white" />}
        gradient="from-[#FF8E00] to-[#D5007D]"
        accents={['#EA3C3A', '#80C51F']}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#F0EFEB] border border-[#E8E6E1]">
          <TabsTrigger value="paiements" className="data-[state=active]:bg-[#2D5A4C] data-[state=active]:text-white" data-testid="tab-paiements">
            <CurrencyDollar size={16} className="mr-1.5" /> Paiements
          </TabsTrigger>
          <TabsTrigger value="echeanciers" className="data-[state=active]:bg-[#2D5A4C] data-[state=active]:text-white" data-testid="tab-echeanciers">
            <CalendarBlank size={16} className="mr-1.5" /> Echeanciers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="paiements" className="space-y-6 mt-4">

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CurrencyDollar size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]">{fmtEUR(totalAttendu)} €</p>
              <p className="text-xs text-[#5E6D66]">Total attendu</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#2D5A4C]">{fmtEUR(totalRecu)} €</p>
              <p className="text-xs text-[#5E6D66]">Total reçu</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock size={24} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#E8A365]">{fmtEUR(totalEnAttente)} €</p>
              <p className="text-xs text-[#5E6D66]">En attente</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Select value={filterStatut} onValueChange={(v) => setFilterStatut(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px] border-[#E8E6E1]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {statutOptions.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterType} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px] border-[#E8E6E1]">
                <SelectValue placeholder="Type de flux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {typeFluxOptions.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5E6D66]">Aucun paiement trouvé</p>
              <Button onClick={() => setDialogOpen(true)} variant="link" className="text-[#2D5A4C] mt-2">
                Créer votre premier paiement
              </Button>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E8E6E1]">
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66] whitespace-nowrap">Référence</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66] whitespace-nowrap">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66] whitespace-nowrap">Organisation</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66] whitespace-nowrap">Montant</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66] whitespace-nowrap">Échéance</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66] whitespace-nowrap">Statut</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66] text-right whitespace-nowrap">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} className="border-[#E8E6E1] hover:bg-[#F9F8F5]" data-testid={`payment-row-${payment.id}`}>
                    <TableCell>
                      <span className="font-mono text-sm">{payment.reference_interne}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {typeFluxOptions.find(t => t.value === payment.type_flux)?.label || payment.type_flux}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-[#1A2421]">
                      {getOrgName(payment.organization_id)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-[#1A2421]">{fmtEUR(payment.montant_attendu)} €</p>
                        {payment.montant_recu > 0 && (
                          <p className="text-xs text-[#2D5A4C]">Reçu: {fmtEUR(payment.montant_recu)} €</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#5E6D66]">
                      {payment.date_echeance ? new Date(payment.date_echeance).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      {getStatutBadge(payment.statut)}
                      {payment.mode_reglement && (
                        <Badge variant="outline" className={`text-[9px] mt-0.5 ml-0 block w-fit ${
                          payment.mode_reglement === 'paypal' ? 'border-[#003087] text-[#003087]' :
                          payment.mode_reglement === 'stripe' ? 'border-[#635BFF] text-[#635BFF]' :
                          payment.mode_reglement === 'virement' ? 'border-[#2D5A4C] text-[#2D5A4C]' :
                          payment.mode_reglement === 'sepa_core' ? 'border-[#00AEEF] text-[#00AEEF]' :
                          payment.mode_reglement === 'sepa_b2b' ? 'border-[#7C3AED] text-[#7C3AED]' :
                          'border-[#5E6D66] text-[#5E6D66]'
                        }`} data-testid={`payment-mode-${payment.id}`}>
                          {payment.mode_reglement === 'paypal' ? 'PayPal' :
                           payment.mode_reglement === 'stripe' ? 'Stripe' :
                           payment.mode_reglement === 'virement' ? 'Virement' :
                           payment.mode_reglement === 'cheque' ? 'Cheque' :
                           payment.mode_reglement === 'especes' ? 'Especes' :
                           payment.mode_reglement === 'sepa_core' ? 'SEPA CORE' :
                           payment.mode_reglement === 'sepa_b2b' ? 'SEPA B2B' :
                           payment.mode_reglement}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {(payment.statut === 'en_attente' || payment.statut === 'partiel') && (
                          <>
                            {/* Online gateways */}
                            {gateways.stripe?.enabled && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOnlinePayment(payment, 'stripe')}
                                disabled={checkoutLoading === `${payment.id}-stripe`}
                                className="border-[#635BFF] text-[#635BFF] hover:bg-[#635BFF]/10 h-7 text-xs px-2"
                                data-testid={`stripe-pay-${payment.id}`}
                              >
                                {checkoutLoading === `${payment.id}-stripe` ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <CreditCard size={12} className="mr-1" />
                                )}
                                Stripe
                              </Button>
                            )}
                            {gateways.paypal?.enabled && gateways.paypal?.configured && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOnlinePayment(payment, 'paypal')}
                                disabled={checkoutLoading === `${payment.id}-paypal`}
                                className="border-[#003087] text-[#003087] hover:bg-[#003087]/10 h-7 text-xs px-2"
                                data-testid={`paypal-pay-${payment.id}`}
                              >
                                {checkoutLoading === `${payment.id}-paypal` ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <CreditCard size={12} className="mr-1" />
                                )}
                                PayPal
                              </Button>
                            )}
                            {/* Stripe Link email */}
                            {gateways.stripe?.enabled && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => setLinkDialog({ open: true, payment, email: '', type: 'stripe' })}
                                className="border-[#2D5A4C] text-[#2D5A4C] hover:bg-[#2D5A4C]/10 h-7 text-xs px-2"
                                data-testid={`stripe-link-${payment.id}`}
                                title="Envoyer lien Stripe par email"
                              >
                                <EnvelopeSimple size={12} className="mr-1" /> Lien
                              </Button>
                            )}
                            {/* Bank transfer */}
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setLinkDialog({ open: true, payment, email: '', type: 'virement' })}
                              className="border-[#8DB0A1] text-[#5E6D66] hover:bg-[#8DB0A1]/10 h-7 text-xs px-2"
                              data-testid={`bank-transfer-${payment.id}`}
                              title="Envoyer RIB par email"
                            >
                              <Bank size={12} className="mr-1" /> Virement
                            </Button>
                            {/* SEPA CORE */}
                            <Button
                              size="sm" variant="outline"
                              onClick={() => openSepaDialog(payment, 'CORE')}
                              className="border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF]/10 h-7 text-xs px-2"
                              data-testid={`sepa-core-${payment.id}`}
                              title="Prelevement SEPA CORE"
                            >
                              <ListChecks size={12} className="mr-1" /> SEPA
                            </Button>
                            {/* SEPA B2B */}
                            <Button
                              size="sm" variant="outline"
                              onClick={() => openSepaDialog(payment, 'B2B')}
                              className="border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/10 h-7 text-xs px-2"
                              data-testid={`sepa-b2b-${payment.id}`}
                              title="Prelevement SEPA B2B"
                            >
                              <ListChecks size={12} className="mr-1" /> B2B
                            </Button>
                          </>
                        )}
                        {(payment.statut === 'en_attente' || payment.statut === 'partiel') && (
                          <Button 
                            size="sm" 
                            onClick={() => openReceiveDialog(payment)}
                            className="bg-[#2D5A4C] hover:bg-[#1E4136] h-7 text-xs px-2"
                            data-testid={`receive-payment-${payment.id}`}
                          >
                            <Receipt size={12} className="mr-1" />
                            Encaisser
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadRecu(payment.id)}
                          data-testid={`download-recu-${payment.id}`}
                          title="Telecharger recu PDF"
                          className="h-7 px-1.5"
                        >
                          <FilePdf size={14} className="text-[#D47A62]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>

            {/* Mobile card view for payments */}
            <div className="sm:hidden space-y-2" data-testid="payments-mobile-cards">
              {payments.map((payment) => (
                <Card key={payment.id} className="border-[#E8E6E1]" data-testid={`payment-card-${payment.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-mono text-sm font-semibold text-[#1A2421]">{payment.reference_interne}</p>
                        <p className="text-[11px] text-[#5E6D66]">{getOrgName(payment.organization_id)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-[#1A2421]">{fmtEUR(payment.montant_attendu)} EUR</p>
                        {payment.montant_recu > 0 && <p className="text-[10px] text-[#2D5A4C]">Recu: {fmtEUR(payment.montant_recu)} EUR</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatutBadge(payment.statut)}
                      <Badge variant="outline" className="text-[9px]">{typeFluxOptions.find(t => t.value === payment.type_flux)?.label || payment.type_flux}</Badge>
                      {payment.date_echeance && <span className="text-[10px] text-[#5E6D66]">{new Date(payment.date_echeance).toLocaleDateString('fr-FR')}</span>}
                      {payment.mode_reglement && (
                        <Badge variant="outline" className={`text-[9px] ${
                          payment.mode_reglement === 'paypal' ? 'border-[#003087] text-[#003087]' :
                          payment.mode_reglement === 'stripe' ? 'border-[#635BFF] text-[#635BFF]' :
                          'border-[#5E6D66] text-[#5E6D66]'
                        }`}>{payment.mode_reglement}</Badge>
                      )}
                    </div>
                    {(payment.statut === 'en_attente' || payment.statut === 'partiel') && (
                      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[#F0EFEB]">
                        {gateways.stripe?.enabled && (
                          <Button size="sm" variant="outline" onClick={() => handleOnlinePayment(payment, 'stripe')} disabled={checkoutLoading === `${payment.id}-stripe`} className="border-[#635BFF] text-[#635BFF] h-8 text-xs flex-1">
                            <CreditCard size={12} className="mr-1" /> Stripe
                          </Button>
                        )}
                        {gateways.paypal?.enabled && gateways.paypal?.configured && (
                          <Button size="sm" variant="outline" onClick={() => handleOnlinePayment(payment, 'paypal')} disabled={checkoutLoading === `${payment.id}-paypal`} className="border-[#003087] text-[#003087] h-8 text-xs flex-1">
                            <CreditCard size={12} className="mr-1" /> PayPal
                          </Button>
                        )}
                        <Button size="sm" onClick={() => openReceiveDialog(payment)} className="bg-[#2D5A4C] hover:bg-[#1E4136] h-8 text-xs flex-1">
                          <Receipt size={12} className="mr-1" /> Encaisser
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            </>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="echeanciers" className="space-y-6 mt-4">
          {/* Echeanciers header */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#5E6D66]">Suivi des echeanciers avec progression et historique des relances</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleTriggerReminders}
              disabled={triggeringReminders}
              className="border-[#2D5A4C] text-[#2D5A4C] hover:bg-[#2D5A4C]/10"
              data-testid="trigger-reminders-btn"
            >
              {triggeringReminders ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Lightning size={16} className="mr-1.5" weight="fill" />}
              Scanner les relances
            </Button>
          </div>

          {/* Schedules list */}
          {schedulesLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" /></div>
          ) : schedules.length === 0 ? (
            <Card className="border-[#E8E6E1]">
              <CardContent className="py-12 text-center">
                <CalendarBlank size={32} className="mx-auto text-[#E8E6E1] mb-3" />
                <p className="text-[#5E6D66] text-sm">Aucun echeancier</p>
                <Button onClick={() => setScheduleDialog(true)} variant="link" className="text-[#2D5A4C] mt-1 text-sm">Creer un echeancier</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="schedules-tracking-list">
              {schedules.map((schedule) => {
                const isExpanded = expandedSchedule === schedule.id;
                const paidCount = (schedule.payments || []).filter(p => p.statut === 'recu').length;
                const totalCount = (schedule.payments || []).length;
                const progression = schedule.progression || 0;
                const scheduleReminders = remindersHistory.filter(r => r.schedule_id === schedule.id);

                return (
                  <Card key={schedule.id} className="border-[#E8E6E1] overflow-hidden" data-testid={`schedule-card-${schedule.id}`}>
                    <div
                      className="flex items-center gap-4 p-4 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
                      onClick={() => setExpandedSchedule(isExpanded ? null : schedule.id)}
                    >
                      {isExpanded ? <CaretDown size={18} className="text-[#5E6D66]" /> : <CaretRight size={18} className="text-[#5E6D66]" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-[#1A2421]">{schedule.reference}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {typeFluxOptions.find(t => t.value === schedule.type_flux)?.label || schedule.type_flux}
                          </Badge>
                        </div>
                        <p className="text-xs text-[#5E6D66] mt-0.5">
                          {getOrgName(schedule.organization_id)} — Debut: {schedule.start_date}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-bold text-[#1A2421]">{fmtEUR(schedule.total_amount)} €</p>
                          <p className="text-[11px] text-[#5E6D66]">{paidCount}/{totalCount} echeances</p>
                        </div>
                        <div className="w-24">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-medium text-[#5E6D66]">{progression}%</span>
                          </div>
                          <div className="h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${progression >= 100 ? 'bg-emerald-500' : progression > 50 ? 'bg-[#2D5A4C]' : 'bg-amber-500'}`}
                              style={{ width: `${Math.min(100, progression)}%` }}
                            />
                          </div>
                        </div>
                        {scheduleReminders.length > 0 && (
                          <Badge className="bg-[#00AEEF]/10 text-[#00AEEF] text-[10px]">
                            <Bell size={10} className="mr-0.5" /> {scheduleReminders.length}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-[#E8E6E1]">
                        {/* Payments detail table */}
                        <div className="p-4">
                          <p className="text-xs font-semibold text-[#1A2421] uppercase mb-3">Detail des echeances</p>
                          <Table>
                            <TableHeader>
                              <TableRow className="border-[#E8E6E1]">
                                <TableHead className="text-[10px] uppercase text-[#5E6D66]">#</TableHead>
                                <TableHead className="text-[10px] uppercase text-[#5E6D66]">Type</TableHead>
                                <TableHead className="text-[10px] uppercase text-[#5E6D66]">Montant</TableHead>
                                <TableHead className="text-[10px] uppercase text-[#5E6D66]">Echeance</TableHead>
                                <TableHead className="text-[10px] uppercase text-[#5E6D66]">Statut</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(schedule.payments || []).map((p, idx) => {
                                const isOverdue = p.statut !== 'recu' && p.date_echeance && new Date(p.date_echeance) < new Date();
                                return (
                                  <TableRow key={p.id} className={`border-[#E8E6E1] ${isOverdue ? 'bg-red-50/50' : ''}`}>
                                    <TableCell className="text-xs text-[#5E6D66]">{idx + 1}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className="text-[10px]">
                                        {p.type_flux === 'acompte' ? 'Acompte' : `Ech. ${idx + (schedule.acompte_amount > 0 ? 0 : 1)}`}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-xs font-medium text-[#1A2421]">
                                      {fmtEUR(p.montant_attendu)} €
                                      {(p.montant_recu || 0) > 0 && (
                                        <span className="text-[#2D5A4C] ml-1">(recu: {fmtEUR(p.montant_recu)} €)</span>
                                      )}
                                    </TableCell>
                                    <TableCell className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-[#5E6D66]'}`}>
                                      {p.date_echeance ? new Date(p.date_echeance).toLocaleDateString('fr-FR') : '-'}
                                      {isOverdue && <Warning size={12} className="inline ml-1 text-red-500" />}
                                    </TableCell>
                                    <TableCell>{getStatutBadge(p.statut)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Reminder history for this schedule */}
                        {scheduleReminders.length > 0 && (
                          <div className="px-4 pb-4">
                            <p className="text-xs font-semibold text-[#1A2421] uppercase mb-2">Historique des relances</p>
                            <div className="space-y-1.5 max-h-40 overflow-y-auto">
                              {scheduleReminders.map((r) => (
                                <div key={r.id} className="flex items-center gap-2 p-2 rounded bg-[#F9F8F5] text-xs">
                                  <Badge className={`text-[9px] ${r.channels?.some(c => c.success) ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                    {r.delay_label}
                                  </Badge>
                                  <span className="text-[#5E6D66] font-mono">{r.reference}</span>
                                  <span className="text-[#5E6D66]">{fmtEUR(r.montant)} €</span>
                                  <div className="flex gap-1 ml-auto">
                                    {(r.channels || []).map((ch, ci) => (
                                      <Badge key={ci} variant="outline" className={`text-[9px] ${ch.success ? 'border-emerald-300 text-emerald-600' : 'border-red-300 text-red-500'}`}>
                                        {ch.type === 'email' ? 'Email' : 'SMS'} {ch.success ? '✓' : '✗'}
                                      </Badge>
                                    ))}
                                  </div>
                                  <span className="text-[10px] text-[#8DB0A1]">
                                    {r.sent_at ? new Date(r.sent_at).toLocaleDateString('fr-FR') : ''}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Global reminders history */}
          <Card className="border-[#E8E6E1]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Historique des relances</CardTitle>
            </CardHeader>
            <CardContent>
              {remindersLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" /></div>
              ) : remindersHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Bell size={24} className="mx-auto text-[#E8E6E1] mb-2" />
                  <p className="text-sm text-[#5E6D66]">Aucune relance envoyee</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto" data-testid="reminders-history-list">
                  {remindersHistory.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#F9F8F5] text-xs hover:bg-[#F0EFEB] transition-colors">
                      <Badge className={`text-[9px] min-w-[40px] justify-center ${
                        r.delay_days < 0 ? 'bg-blue-100 text-blue-700' : r.delay_days === 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {r.delay_label}
                      </Badge>
                      <span className="font-mono text-[#1A2421]">{r.reference}</span>
                      <span className="text-[#5E6D66] truncate max-w-[120px]">{r.organization_name}</span>
                      <span className="font-medium text-[#1A2421]">{fmtEUR(r.montant)} €</span>
                      <div className="flex gap-1 ml-auto">
                        {(r.channels || []).map((ch, ci) => (
                          <Badge key={ci} variant="outline" className={`text-[9px] ${ch.success ? 'border-emerald-300 text-emerald-600' : 'border-red-300 text-red-500'}`}>
                            {ch.type === 'email' ? <EnvelopeSimple size={10} className="mr-0.5" /> : <Bell size={10} className="mr-0.5" />}
                            {ch.type === 'email' ? 'Email' : 'SMS'}
                          </Badge>
                        ))}
                      </div>
                      <span className="text-[10px] text-[#8DB0A1] whitespace-nowrap">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Nouveau paiement attendu</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de flux *</Label>
                <Select value={formData.type_flux} onValueChange={(v) => setFormData({ ...formData, type_flux: v })} required>
                  <SelectTrigger data-testid="select-type-flux">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {typeFluxOptions.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Montant attendu (€) *</Label>
                <CurrencyInput
                  value={formData.montant_attendu}
                  onChange={(n) => setFormData({ ...formData, montant_attendu: n == null ? '' : n })}
                  currency="€"
                  testId="input-montant"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Organisation *</Label>
              <Select value={formData.organization_id} onValueChange={(v) => setFormData({ ...formData, organization_id: v })} required>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.raison_sociale}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dossiers liés</Label>
                <div className="border border-[#E8E6E1] rounded-md p-2 max-h-40 overflow-y-auto" data-testid="input-dossier-multi">
                  {dossiers.length === 0 ? (
                    <p className="text-xs text-[#5E6D66] italic">Aucun dossier disponible</p>
                  ) : dossiers.map(d => {
                    const checked = (formData.dossier_ids || []).includes(d.id);
                    return (
                      <label key={d.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-[#F9F8F5] rounded px-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(formData.dossier_ids || []);
                            if (e.target.checked) next.add(d.id); else next.delete(d.id);
                            setFormData({ ...formData, dossier_ids: Array.from(next) });
                          }}
                          data-testid={`dossier-check-${d.id}`}
                        />
                        <span className="font-medium">{d.numero_dossier}</span>
                        {d.objet_besoin && <span className="text-xs text-[#5E6D66] truncate">— {d.objet_besoin}</span>}
                      </label>
                    );
                  })}
                </div>
                {(formData.dossier_ids || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {formData.dossier_ids.map(id => (
                      <Badge key={id} variant="outline" className="text-xs">{getDossierNum(id)}</Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Date d'échéance</Label>
                <Input
                  type="date"
                  value={formData.date_echeance}
                  onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Mode de règlement prévu</Label>
              <Select value={formData.mode_reglement} onValueChange={(v) => setFormData({ ...formData, mode_reglement: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {modeReglementOptions.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-payment-btn">
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receive Payment Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Enregistrer un paiement reçu</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant reçu (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={receiveAmount}
                  onChange={(e) => setReceiveAmount(e.target.value)}
                  data-testid="input-receive-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de règlement</Label>
                <Input
                  type="date"
                  value={receiveDate}
                  onChange={(e) => setReceiveDate(e.target.value)}
                  data-testid="input-receive-date"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mode de paiement</Label>
              <Select value={receiveMode} onValueChange={setReceiveMode}>
                <SelectTrigger data-testid="select-receive-mode">
                  <SelectValue placeholder="Sélectionner un mode" />
                </SelectTrigger>
                <SelectContent>
                  {modeReglementOptions.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Référence virement/transaction</Label>
              <Input
                value={receiveReference}
                onChange={(e) => setReceiveReference(e.target.value)}
                placeholder="Optionnel"
                data-testid="input-receive-reference"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleReceive} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="confirm-receive-btn">
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Dialog (Stripe link or Bank transfer) */}
      <Dialog open={linkDialog.open} onOpenChange={(v) => !v && setLinkDialog({ open: false, payment: null, email: '', type: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {linkDialog.type === 'stripe' ? 'Envoyer un lien de paiement Stripe' : 'Envoyer les informations de virement'}
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {linkDialog.payment && (
              <div className="p-3 bg-[#F9F8F5] rounded-lg text-sm">
                <p className="font-medium text-[#1A2421]">{linkDialog.payment.reference_interne}</p>
                <p className="text-[#5E6D66]">
                  {fmtEUR((linkDialog.payment.montant_attendu || 0) - (linkDialog.payment.montant_recu || 0))} € restant
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Email du destinataire *</Label>
              <Input
                type="email"
                value={linkDialog.email}
                onChange={(e) => setLinkDialog(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contact@organisation.com"
                data-testid="link-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog({ open: false, payment: null, email: '', type: '' })}>Annuler</Button>
            <Button onClick={handleSendLink} disabled={actionLoading === 'link' || !linkDialog.email} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="send-link-btn">
              {actionLoading === 'link' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <EnvelopeSimple size={16} className="mr-1.5" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEPA Dialog */}
      <Dialog open={sepaDialog.open} onOpenChange={(v) => !v && setSepaDialog({ open: false, payment: null, sepaType: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Prelevement SEPA {sepaDialog.sepaType && (
                <Badge className={sepaDialog.sepaType === 'B2B' ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'bg-[#00AEEF]/10 text-[#00AEEF]'}>
                  {sepaDialog.sepaType}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              {sepaDialog.sepaType === 'B2B'
                ? 'Prelevement inter-entreprises (pas de droit de remboursement, execution J+1)'
                : 'Prelevement standard (droit de remboursement 8 semaines, execution J+2)'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {sepaDialog.payment && (
              <div className="p-3 bg-[#F9F8F5] rounded-lg text-sm">
                <p className="font-medium text-[#1A2421]">{sepaDialog.payment.reference_interne}</p>
                <p className="text-[#5E6D66]">
                  {fmtEUR((sepaDialog.payment.montant_attendu || 0) - (sepaDialog.payment.montant_recu || 0))} € a prelever
                </p>
              </div>
            )}
            {sepaMandates.length === 0 ? (
              <div className="p-4 text-center text-sm text-[#5E6D66] bg-amber-50 rounded-lg">
                <Warning size={20} className="mx-auto mb-2 text-amber-500" />
                Aucun mandat SEPA {sepaDialog.sepaType || ''} actif pour cette organisation. Creez un mandat dans les parametres SEPA.
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Mandat SEPA {sepaDialog.sepaType || ''} actif</Label>
                <Select value={selectedMandate} onValueChange={setSelectedMandate}>
                  <SelectTrigger data-testid="sepa-mandate-select"><SelectValue placeholder="Selectionner un mandat" /></SelectTrigger>
                  <SelectContent>
                    {sepaMandates.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.rum} ({m.type_sepa}) - {m.nom_debiteur}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSepaDialog({ open: false, payment: null, sepaType: null })}>Annuler</Button>
            <Button
              onClick={handleSepaDebit}
              disabled={actionLoading === 'sepa' || !selectedMandate || sepaMandates.length === 0}
              className={sepaDialog.sepaType === 'B2B' ? 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white' : 'bg-[#00AEEF] hover:bg-[#009DD8] text-white'}
              data-testid="confirm-sepa-btn"
            >
              {actionLoading === 'sepa' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ListChecks size={16} className="mr-1.5" />}
              Initier le prelevement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialog} onOpenChange={setScheduleDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Creer un echeancier</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Organisation *</Label>
                <Select value={scheduleForm.organization_id} onValueChange={(v) => setScheduleForm(prev => ({ ...prev, organization_id: v }))}>
                  <SelectTrigger className="text-xs"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                  <SelectContent>
                    {organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.raison_sociale}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Dossier (optionnel)</Label>
                <Select value={scheduleForm.dossier_id || '_none'} onValueChange={(v) => setScheduleForm(prev => ({ ...prev, dossier_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="text-xs"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Aucun</SelectItem>
                    {dossiers.map(d => <SelectItem key={d.id} value={d.id}>{d.numero_dossier}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Montant total (€) *</Label>
                <Input type="number" step="0.01" value={scheduleForm.total_amount} onChange={(e) => setScheduleForm(prev => ({ ...prev, total_amount: e.target.value }))} className="text-xs" data-testid="schedule-total" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Type de flux</Label>
                <Select value={scheduleForm.type_flux} onValueChange={(v) => setScheduleForm(prev => ({ ...prev, type_flux: v }))}>
                  <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cotisation_ftpe">Cotisation FTPE</SelectItem>
                    <SelectItem value="contribution">Contribution</SelectItem>
                    <SelectItem value="subvention">Subvention</SelectItem>
                    <SelectItem value="prestation_service">Prestation</SelectItem>
                    <SelectItem value="formation">Formation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Acompte type</Label>
                <Select value={scheduleForm.acompte_type} onValueChange={(v) => setScheduleForm(prev => ({ ...prev, acompte_type: v }))}>
                  <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">% du total</SelectItem>
                    <SelectItem value="fixed">Montant fixe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Valeur acompte {scheduleForm.acompte_type === 'percent' ? '(%)' : '(€)'}</Label>
                <Input type="number" step="0.01" value={scheduleForm.acompte_value} onChange={(e) => setScheduleForm(prev => ({ ...prev, acompte_value: e.target.value }))} className="text-xs" data-testid="schedule-acompte" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Nb echeances</Label>
                <Input type="number" min="1" max="36" value={scheduleForm.nb_echeances} onChange={(e) => setScheduleForm(prev => ({ ...prev, nb_echeances: e.target.value }))} className="text-xs" data-testid="schedule-nb" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Date de debut</Label>
              <Input type="date" value={scheduleForm.start_date} onChange={(e) => setScheduleForm(prev => ({ ...prev, start_date: e.target.value }))} className="text-xs" />
            </div>
            {/* Preview */}
            {scheduleForm.total_amount && parseFloat(scheduleForm.total_amount) > 0 && (
              <div className="p-3 bg-[#F9F8F5] rounded-lg text-xs space-y-1">
                <p className="font-semibold text-[#1A2421]">Apercu :</p>
                {(() => {
                  const total = parseFloat(scheduleForm.total_amount);
                  const acompte = scheduleForm.acompte_type === 'percent'
                    ? total * (parseFloat(scheduleForm.acompte_value || 0) / 100)
                    : Math.min(parseFloat(scheduleForm.acompte_value || 0), total);
                  const rest = total - acompte;
                  const nb = parseInt(scheduleForm.nb_echeances || 1);
                  const ech = rest / nb;
                  return (
                    <>
                      {acompte > 0 && <p className="text-[#5E6D66]">Acompte : <strong>{acompte.toFixed(2)} €</strong></p>}
                      <p className="text-[#5E6D66]">{nb} echeance(s) de <strong>{ech.toFixed(2)} €</strong></p>
                      <p className="text-[#5E6D66]">Total : <strong>{total.toFixed(2)} €</strong></p>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateSchedule} disabled={actionLoading === 'schedule'} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="create-schedule-btn">
              {actionLoading === 'schedule' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CalendarBlank size={16} className="mr-1.5" />}
              Creer l'echeancier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
